import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowClockwise, ArrowRight, ArrowSquareOut, Info, MagnifyingGlass, Warning } from 'shared/icons';
import { GetCampaignTypeLabel } from '../../constants/campaignTypes';
import api from '../../services/api';
import { FormatInt, FormatMoney } from '../../utils/format';
import { MLB_URLS, SecureMlImageUrl } from '../../utils/ml';
import PaginationControls from '../PaginationControls';
import Button from '../ui/Button';
import LoadingSpinner from '../ui/LoadingSpinner';
import { ItemId, Sku } from '../ui/Mono';
import {
    APPLY_NOW_SORTS,
    BuildApplyNowRows,
    FilterApplyNowRows,
    IsApplyNowMarkMissing,
    SortApplyNowRows,
    SummarizeApplyNowRows,
} from './applyNowSwitches';
import { DASH, DeltaMoney, FormatPercentOrDash, IsNum, MetricRow, Pill } from './campaignCardShared';

/**
 * APLIQUE AGORA — a fila de trabalho do dia.
 *
 * Uma linha por anúncio: a campanha de HOJE ao lado da campanha que vale a pena
 * aplicar AGORA, sendo "vale a pena" a única definição que não pede julgamento —
 * vantagem explícita de um lado sem prejuízo do outro. A régua mora no backend
 * (`backend_v2/src/modules/campaigns/applyNowRule.js`), que marca cada campanha
 * (`is_applicable_now`, `switch_advantage`, `switch_rank`); ela saiu daqui em
 * 29/09 para o card da Visão geral contar a MESMA fila que esta tela lista.
 *
 * Só leitura, como o Radar de Rebate: a adesão continua sendo feita no painel do
 * Mercado Livre (regra do dono). Por isso cada linha leva o atalho para a
 * central de promoções DAQUELE anúncio — a tela existe para dizer onde clicar,
 * não para clicar por ninguém.
 *
 * Os dados vêm do MESMO `/campaigns/items` do Monitor Unificado, que já devolve
 * as campanhas elegíveis de cada anúncio com `seller_gain`/`consumer_savings`
 * calculados e as marcas da régua. Nenhuma conta de dinheiro nem régua aqui
 * (CLAUDE.md §4): `applyNowSwitches.js` só consome as marcas e ordena a fila.
 */

// Universo carregado: os anúncios ATIVOS, de uma vez só — mesma estratégia do
// Monitor Unificado (o recorte depois é em memória, sem novo request). O cap de
// 1000 é o da API; passando disso a tela avisa que a fila está truncada.
const ITEMS_QUERY = 'status=active&limit=1000&offset=0';

const GRID =
    'grid grid-cols-1 gap-2 lg:grid-cols-[minmax(0,2.3fr)_minmax(0,1.3fr)_auto_minmax(0,1.3fr)_minmax(0,1.05fr)_minmax(0,0.9fr)] lg:items-center';

/** Rótulo do status da participação — o que falta fazer nesta campanha. */
const ITEM_STATUS_PILL = {
    candidate: { label: 'convite do ML', tone: 'info' },
    pending: { label: 'adesão em processamento', tone: 'warning' },
};

/**
 * De que lado está a vantagem. Vai na linha porque as três são aplicáveis mas
 * NÃO são a mesma decisão: "ganho mais e o cliente nem sente" é dinheiro no
 * bolso, "o cliente paga menos e eu não perco" é competitividade.
 */
const ADVANTAGE_PILL = {
    both: { label: 'ganho dos dois lados', tone: 'success' },
    seller: { label: 'líquido sobe, cliente paga o mesmo', tone: 'success' },
    consumer: { label: 'cliente paga menos, líquido igual', tone: 'info' },
};

/** Nome de campanha sem nome (DOD/LIGHTNING vêm assim do ML): cai no tipo. */
const campaignLabel = (campaign) =>
    campaign?.campaign_name || GetCampaignTypeLabel(campaign?.campaign_type, '') || campaign?.campaign_id || DASH;

/** Cabeçalho de célula no empilhado (mobile); no lg quem rotula é o header. */
const CellLabel = ({ children }) => (
    <p className="mb-0.5 text-[9px] font-bold uppercase tracking-wide text-mono-500 lg:hidden">{children}</p>
);

/** Bloco de preços de um lado da troca. Sem conta local: tudo já vem pronto. */
const PriceSide = ({ title, subtitle, priceConsumer, priceNet, rebatePercent, consumerDelta, netDelta, tone }) => (
    <div className={`min-w-0 rounded-lg border px-2 py-1.5 ${tone}`}>
        <p className="truncate text-[11px] font-semibold text-black dark:text-white" title={title}>
            {title}
        </p>
        <p className="mb-1 truncate text-[9px] italic text-mono-500">{subtitle}</p>
        <MetricRow
            label="Cliente"
            value={FormatMoney(priceConsumer)}
            // Economia vai como PREFIXO do preço que ela modifica (regra do
            // dono). `signReflectsPrice`: economizar mostra "−R$", porque o que
            // se lê ao lado é PREÇO, não ganho.
            prefix={consumerDelta === undefined ? null : <DeltaMoney value={consumerDelta} signReflectsPrice dashWhenAbsent={false} />}
        />
        <MetricRow
            label="Líquido"
            value={FormatMoney(priceNet)}
            prefix={netDelta === undefined ? null : <DeltaMoney value={netDelta} dashWhenAbsent={false} />}
        />
        <MetricRow label="Rebate" value={FormatPercentOrDash(rebatePercent)} />
    </div>
);

const ApplyNowRow = ({ row }) => {
    const { item, target } = row;
    const thumb = SecureMlImageUrl(item.thumbnail);
    const statusPill = ITEM_STATUS_PILL[target.item_status];
    const advantagePill = ADVANTAGE_PILL[row.advantage];

    return (
        <div className={`${GRID} border-b border-mono-100 px-2 py-2 last:border-b-0 dark:border-mono-900`}>
            {/* 1) ANÚNCIO */}
            <div className="flex min-w-0 items-start gap-2">
                {thumb ? (
                    <img src={thumb} alt="" className="h-9 w-9 flex-none rounded object-cover" loading="lazy" />
                ) : (
                    <div className="h-9 w-9 flex-none rounded bg-mono-100 dark:bg-mono-900" />
                )}
                <div className="min-w-0">
                    <Link
                        to={`/campaigns/items/${encodeURIComponent(item.item_id)}`}
                        className="block truncate text-[12px] font-medium text-black hover:underline dark:text-white"
                        title={item.title || item.item_id}
                    >
                        {item.title || item.item_id}
                    </Link>
                    <div className="flex flex-wrap items-center gap-x-2 text-[10px] text-mono-500">
                        <ItemId value={item.item_id} permalink={item.permalink} />
                        {item.sku ? <Sku value={item.sku} /> : null}
                        <a
                            href={MLB_URLS.promos(item.item_id)}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-0.5 text-info-600 hover:underline dark:text-info-400"
                        >
                            aplicar no ML <ArrowSquareOut size={10} />
                        </a>
                    </div>
                </div>
            </div>

            {/* 2) HOJE — os números do item SÃO a base contra a qual o backend
                calculou os deltas (campanha ativa ou, sem nenhuma, o anúncio). */}
            <div className="min-w-0">
                <CellLabel>Hoje</CellLabel>
                <PriceSide
                    title={item.campaign_name || item.campaign_id || 'Sem campanha'}
                    subtitle={
                        item.campaign_id
                            ? GetCampaignTypeLabel(item.campaign_type)
                            : 'preço do anúncio, sem campanha ativa'
                    }
                    priceConsumer={item.price_consumer}
                    priceNet={item.price_net}
                    rebatePercent={item.rebate_percent}
                    tone="border-mono-200 bg-mono-50 dark:border-mono-800 dark:bg-mono-900/40"
                />
            </div>

            {/* 3) seta — só no lado a lado; empilhado ela não orienta nada */}
            <ArrowRight size={16} className="mx-1 hidden flex-none text-mono-300 lg:block dark:text-mono-700" />

            {/* 4) APLIQUE AGORA */}
            <div className="min-w-0">
                <CellLabel>Aplique agora</CellLabel>
                <PriceSide
                    title={campaignLabel(target)}
                    subtitle={GetCampaignTypeLabel(target.campaign_type)}
                    priceConsumer={target.price_consumer}
                    priceNet={target.price_net}
                    rebatePercent={target.rebate_percent}
                    consumerDelta={row.consumer_savings}
                    netDelta={row.net_gain}
                    tone="border-success-200 bg-success-soft/50 dark:border-success-900 dark:bg-success-900/10"
                />
                <div className="mt-1 flex flex-wrap items-center gap-1">
                    {advantagePill ? <Pill tone={advantagePill.tone}>{advantagePill.label}</Pill> : null}
                    {statusPill ? <Pill tone={statusPill.tone}>{statusPill.label}</Pill> : null}
                    {target.price_is_suggested ? (
                        <Pill
                            tone="warning"
                            title="O ML não fechou preço nesta candidatura: o valor é a sugestão dele (suggested_discounted_price), não uma oferta acordada."
                        >
                            preço estimado
                        </Pill>
                    ) : null}
                    {row.alternatives.length > 0 ? (
                        <Pill
                            tone="mono"
                            title={row.alternatives.map((alternativa) => campaignLabel(alternativa)).join(' · ')}
                        >
                            +{row.alternatives.length} que também melhoram
                        </Pill>
                    ) : null}
                </div>
            </div>

            {/* 5) GANHO — o número que justifica a linha */}
            <div className="min-w-0">
                <CellLabel>Ganho</CellLabel>
                <div className="leading-tight">
                    <p className="text-[13px]">
                        <DeltaMoney value={row.net_gain} title="Quanto EU recebo a mais por venda nesta troca" />
                    </p>
                    <p className="text-[9px] uppercase tracking-wide text-mono-500">no líquido, por venda</p>
                    <p className="mt-1 text-[11px]">
                        <DeltaMoney
                            value={row.consumer_savings}
                            signReflectsPrice
                            title="Quanto o preço ao cliente cai nesta troca"
                        />
                    </p>
                    <p className="text-[9px] uppercase tracking-wide text-mono-500">no preço ao cliente</p>
                </div>
            </div>

            {/* 6) GIRO — o que transforma centavos por venda em dinheiro */}
            <div className="min-w-0">
                <CellLabel>Giro</CellLabel>
                <div className="leading-tight">
                    <p className="text-[12px] font-semibold tabular-nums text-black dark:text-white">
                        {IsNum(row.sales_7d_total) ? FormatInt(row.sales_7d_total) : DASH}
                    </p>
                    <p className="text-[9px] uppercase tracking-wide text-mono-500">vendas 7d</p>
                    <p
                        className="mt-1 text-[12px] font-semibold tabular-nums text-black dark:text-white"
                        title="Ganho por venda × vendas dos últimos 7 dias — estimativa para priorizar, não previsão de receita."
                    >
                        {IsNum(row.impact_7d) ? FormatMoney(row.impact_7d) : DASH}
                    </p>
                    <p className="text-[9px] uppercase tracking-wide text-mono-500">impacto 7d (est.)</p>
                </div>
            </div>
        </div>
    );
};

/** Número grande do resumo. Três medidas, nenhuma inventada. */
const SummaryTile = ({ label, value, hint }) => (
    <div className="rounded-xl border border-mono-200 bg-white px-3 py-2 dark:border-mono-800 dark:bg-mono-950">
        <p className="text-[10px] uppercase tracking-wide text-mono-500">{label}</p>
        <p className="text-xl font-bold tabular-nums text-black dark:text-white">{value}</p>
        {hint ? <p className="text-[10px] text-mono-500">{hint}</p> : null}
    </div>
);

const CampaignApplyNow = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [truncado, setTruncado] = useState(null);

    const [search, setSearch] = useState('');
    const [sortBy, setSortBy] = useState('impact_7d');
    const [allowSuggested, setAllowSuggested] = useState(true);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(30);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await api.get(`/campaigns/items?${ITEMS_QUERY}`);
            const payload = response?.data ?? response;
            const results = Array.isArray(payload?.results) ? payload.results : [];
            // Frontend novo com backend velho: sem a marca, a fila sairia vazia
            // e a tela afirmaria "nenhuma troca aplicável" — falso. Melhor dizer
            // o que falta do que mostrar um vazio enganoso.
            if (IsApplyNowMarkMissing(results)) {
                setItems([]);
                setTruncado(null);
                setError('O backend ainda não envia a marca do Aplique agora — atualize o backend.');
                return;
            }
            setItems(results);
            const totalNoServidor = Number(payload?.paging?.total ?? results.length);
            setTruncado(totalNoServidor > results.length ? { carregados: results.length, total: totalNoServidor } : null);
        } catch (err) {
            console.error('Erro ao carregar a fila de campanhas:', err);
            setError('Não foi possível carregar os anúncios.');
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    // A escolha da campanha depende do corte de preço estimado (ele muda QUAL
    // candidatura é a melhor), então a fila é remontada quando ele muda.
    const rows = useMemo(() => BuildApplyNowRows(items, { allowSuggested }), [items, allowSuggested]);
    const visibleRows = useMemo(
        () => SortApplyNowRows(FilterApplyNowRows(rows, { search }), sortBy),
        [rows, search, sortBy],
    );
    const resumo = useMemo(() => SummarizeApplyNowRows(visibleRows), [visibleRows]);
    const estimadas = useMemo(() => rows.filter((row) => row.target?.price_is_suggested === true).length, [rows]);

    // Qualquer mudança de recorte volta para a primeira página: manter a página
    // 7 de uma fila que agora tem 2 linhas mostraria uma tela vazia.
    useEffect(() => {
        setPage(1);
    }, [search, sortBy, allowSuggested, perPage]);

    const pageRows = visibleRows.slice((page - 1) * perPage, page * perPage);

    return (
        <div className="space-y-3">
            <div className="flex items-start gap-2 rounded-xl border border-info-200 bg-info-soft p-3 text-xs text-info-900 dark:border-info-900 dark:bg-info-950 dark:text-info-200">
                <Info size={16} className="mt-0.5 flex-none" />
                <span>
                    Entra aqui toda campanha com <strong>vantagem explícita e sem contrapartida</strong>: o seu
                    líquido sobe sem o cliente pagar mais, ou o cliente paga menos sem o seu líquido cair — e
                    melhor ainda quando os dois acontecem juntos. Fica de fora só a troca que cobra um lado pelo
                    outro. Entram apenas candidaturas de campanhas <strong>em andamento</strong> (já começaram e
                    ainda não terminaram; dá para aplicar hoje). Esta tela é <strong>só leitura</strong>: a adesão
                    continua sendo feita no painel do Mercado Livre, pelo link de cada linha.
                </span>
            </div>

            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                <SummaryTile
                    label="Anúncios na fila"
                    value={FormatInt(resumo.items)}
                    hint={`${FormatInt(resumo.by_advantage.both)} dos dois lados · ${FormatInt(
                        resumo.by_advantage.seller,
                    )} só líquido · ${FormatInt(resumo.by_advantage.consumer)} só preço`}
                />
                <SummaryTile
                    label="Ganho por venda (soma)"
                    value={FormatMoney(resumo.net_gain)}
                    hint="somando uma venda de cada anúncio da fila"
                />
                <SummaryTile
                    label="Impacto 7d (estimado)"
                    value={FormatMoney(resumo.impact_7d)}
                    hint={
                        resumo.without_sales > 0
                            ? `${FormatInt(resumo.without_sales)} sem venda conhecida ficaram de fora`
                            : 'ganho por venda × vendas dos últimos 7 dias'
                    }
                />
            </div>

            <div className="flex flex-wrap items-center gap-2 rounded-xl border border-mono-200 bg-white p-2.5 dark:border-mono-800 dark:bg-mono-950">
                <div className="relative min-w-[220px] flex-1">
                    <MagnifyingGlass
                        size={14}
                        aria-hidden="true"
                        className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-mono-400"
                    />
                    <input
                        type="search"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        aria-label="Buscar anúncio por MLB, SKU ou título"
                        placeholder="Buscar MLB, SKU ou título"
                        className="w-full rounded-lg border border-mono-300 bg-white py-1.5 pl-8 pr-3 text-xs text-mono-900 outline-none placeholder:text-mono-400 focus:ring-2 focus:ring-info-500 dark:border-mono-700 dark:bg-mono-900 dark:text-mono-100"
                    />
                </div>

                <label className="flex items-center gap-1.5 text-xs text-mono-700 dark:text-mono-300">
                    Ordenar por
                    <select
                        value={sortBy}
                        onChange={(event) => setSortBy(event.target.value)}
                        className="rounded-lg border border-mono-300 bg-white px-2 py-1.5 text-xs text-mono-900 outline-none focus:ring-2 focus:ring-info-500 dark:border-mono-700 dark:bg-mono-900 dark:text-mono-100"
                    >
                        {APPLY_NOW_SORTS.map(([value, label]) => (
                            <option key={value} value={value}>
                                {label}
                            </option>
                        ))}
                    </select>
                </label>

                <label
                    className="flex items-center gap-1.5 text-xs text-mono-700 dark:text-mono-300"
                    title="Candidatura sem preço fechado: o ML manda só um preço sugerido, então o ganho é estimativa."
                >
                    <input
                        type="checkbox"
                        checked={!allowSuggested}
                        onChange={(event) => setAllowSuggested(!event.target.checked)}
                        className="rounded border-mono-300 dark:border-mono-700"
                    />
                    Só preço fechado ({FormatInt(estimadas)} estimadas)
                </label>

                <Button onClick={load} size="sm" disabled={loading}>
                    <ArrowClockwise size={16} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} />
                    Atualizar
                </Button>
            </div>

            {truncado ? (
                <p className="flex items-center gap-2 rounded-lg border border-warning-200 bg-warning-soft px-3 py-2 text-xs text-warning-text dark:border-warning-900 dark:bg-warning-900/20 dark:text-warning-300">
                    <Warning size={14} />
                    Carregados {FormatInt(truncado.carregados)} de {FormatInt(truncado.total)} anúncios ativos — a fila
                    pode estar incompleta.
                </p>
            ) : null}

            {error ? (
                <div className="flex items-start gap-2 rounded-xl border border-danger-200 bg-danger-soft p-4 text-sm text-danger-text dark:border-danger-900 dark:bg-danger-900/20 dark:text-danger-300">
                    <Warning size={18} />
                    <span>{error}</span>
                </div>
            ) : null}

            {loading ? <LoadingSpinner className="py-10" /> : null}

            {!loading && !error && visibleRows.length === 0 ? (
                <p className="rounded-xl border border-mono-200 bg-white p-6 text-sm text-mono-600 dark:border-mono-800 dark:bg-mono-950 dark:text-mono-400">
                    {rows.length === 0
                        ? 'Nenhum anúncio tem hoje uma candidatura que melhore o seu líquido ou o preço ao cliente sem piorar o outro lado. Quando o Mercado Livre abrir uma, ela aparece aqui.'
                        : 'Nenhum anúncio da fila casa com este recorte.'}
                </p>
            ) : null}

            {!loading && !error && visibleRows.length > 0 ? (
                <>
                    <div className="overflow-hidden rounded-xl border border-mono-200 bg-white dark:border-mono-800 dark:bg-mono-950">
                        <div
                            className={`${GRID} hidden border-b border-mono-200 bg-mono-50 px-2 py-1.5 text-[9px] font-bold uppercase tracking-wide text-mono-500 lg:grid dark:border-mono-800 dark:bg-mono-900`}
                        >
                            <span>Anúncio</span>
                            <span>Hoje</span>
                            <span className="w-4" />
                            <span>Aplique agora</span>
                            <span>Ganho</span>
                            <span>Giro</span>
                        </div>
                        {pageRows.map((row) => (
                            <ApplyNowRow key={`${row.item.item_id}-${row.target.campaign_id}`} row={row} />
                        ))}
                    </div>

                    <PaginationControls
                        currentPage={page}
                        totalPages={Math.ceil(visibleRows.length / perPage)}
                        onPageChange={setPage}
                        itemsPerPage={perPage}
                        onItemsPerPageChange={setPerPage}
                        totalItems={visibleRows.length}
                    />

                    {/* Para onde ir quando a troca NÃO passa na régua desta tela —
                        senão a fila curta parece "não há nada", quando na verdade
                        o que há são trocas que pedem decisão. */}
                    <p className="text-[11px] text-mono-500 dark:text-mono-400">
                        A troca com contrapartida — mais rebate em troca de líquido menor, ou preço menor saindo do
                        seu bolso — não entra aqui: ela fica no <strong>Radar de Rebate</strong>, que ordena por
                        diferença de rebate e mostra o efeito no líquido ao lado.
                    </p>
                </>
            ) : null}
        </div>
    );
};

export default CampaignApplyNow;
